import ErrorStackParser from "error-stack-parser";
import {MiniEvent} from "@cdx/common";
import {throttle} from "es-toolkit/compat";
import cdxEnv from "../env";
import {postRequest} from "./request";

type Frame = {fileName?: string; lineNumber?: number; columnNumber?: number; functionName?: string};

export const exceptionEvent = new MiniEvent<void>();

// A failed report must not become an unhandled rejection, which would be reported in turn.
const throttledRequest = throttle(({stackFrames, msg}: {stackFrames: Frame[]; msg: string}) => {
  postRequest("/services/send-error", {
    stackFrames,
    message: msg,
    appVersion: cdxEnv.DEPLOYMENT_LABEL,
  }).catch((e) => console.error("failed to send error", e));
}, 1000);

// A frame-less report still groups by its message; rethrowing here would only make `window.onerror`
// report "Cannot parse given Error object" instead.
const parseStackFrames = (error: Error): Frame[] => {
  try {
    return ErrorStackParser.parse(error);
  } catch {
    return [];
  }
};

// `JSON.stringify` alone turns most non-errors into `{}`, so name the type too. It also throws on
// cycles and returns undefined for `undefined`.
const describeNonError = (value: unknown) => {
  const getType = () => {
    if (value === null || value === undefined) return String(value);
    return (value as object).constructor?.name || typeof value;
  };
  const getJson = () => {
    try {
      return JSON.stringify(value) ?? String(value);
    } catch {
      return String(value);
    }
  };
  return `${getType()} ${getJson()}`;
};

export const postError = (error: Error, msg: string) => {
  throttledRequest({stackFrames: parseStackFrames(error), msg});
};

window.onerror = (event, file, line, col, errorArg) => {
  const msg = typeof event === "string" ? event : event.type;
  if (msg === "Script error.") return;
  if (msg.indexOf("ResizeObserver loop") >= 0) return;
  if (msg.indexOf("Minified exception occurred") >= 0) return;
  if (msg.indexOf("Cannot read property '_currentElement'") >= 0) return;
  setTimeout(() => {
    exceptionEvent.emit();
  });
  // typed as `Error`, but it's whatever was thrown
  const error: unknown = errorArg;
  const fallbackFrames: Frame[] = [
    {fileName: file, lineNumber: line, columnNumber: col, functionName: "unknown"},
  ];
  if (error instanceof Error) {
    const stackFrames = parseStackFrames(error);
    throttledRequest({stackFrames: stackFrames.length ? stackFrames : fallbackFrames, msg});
  } else if (error !== undefined && error !== null) {
    // `throw {...}` arrives as "Uncaught [object Object]"
    throttledRequest({stackFrames: fallbackFrames, msg: `uncaught ${describeNonError(error)}`});
  } else {
    throttledRequest({stackFrames: fallbackFrames, msg});
  }
};

window.addEventListener("unhandledrejection", (event) => {
  const reason: unknown = event.reason;
  if (reason instanceof Error) {
    throttledRequest({
      stackFrames: parseStackFrames(reason),
      msg: `unhandled rejection: ${reason.message}`,
    });
  } else {
    throttledRequest({stackFrames: [], msg: `unhandled rejection: ${describeNonError(reason)}`});
  }
});
